import React from 'react';
import type { Nullable, StringMap } from 'corgi-types';
import { SignupFlow } from 'corgi-types';
import { AuthenticationProvider } from 'graphql-ts-types';

import DynamicForm from 'common/components/DynamicForm';
import FormSubmitButton from 'common/components/Form/FormSubmitButton';
import { useEmailGqlValidations } from 'common/hooks/validation-rules/email';
import { useLocalization } from 'common/providers/LocaleProvider';
import { Event } from 'common/types/auth';
import type { FieldName } from '../enums';
import { useFormGroups } from '../hooks/form-groups';
import { usePerformLoginHandler } from '../hooks/perform-login';

interface FormProps {
  setCommonError: (errorMessage: Nullable<string>) => void;
  onSuccess: ({ queryParams }?: { queryParams?: StringMap }) => void;
  userFlow: SignupFlow;
  withAutoFocus: boolean;
}

const Form = ({ setCommonError, onSuccess, userFlow, withAutoFocus }: FormProps) => {
  const { t } = useLocalization();

  const handleOnSuccess = () => {
    onSuccess(userFlow === SignupFlow.CartHomeClub ? { queryParams: { userFlow } } : undefined);
  };

  const emailvalidationRules = useEmailGqlValidations();
  const formGroups = useFormGroups({
    emailvalidationRules,
    withAutoFocus,
    userFlow,
  });
  const { isSuccessful, performLoginHandler } = usePerformLoginHandler({
    setCommonError,
    onSuccess: handleOnSuccess,
  });
  const onSubmit = async (fieldValues: { [Key in FieldName]: string }) => {
    await performLoginHandler(fieldValues);
  };

  return (
    <DynamicForm
      id="login-form"
      aria-label={t('h24_login_form')}
      formGroups={formGroups}
      formConfig={{
        criteriaMode: 'all',
      }}
      onSubmit={onSubmit}
      renderSubmitButton={props => (
        <FormSubmitButton
          {...props}
          isSuccess={isSuccessful}
          defaultButtonLabel={t('h24_login_form_submit_btn')}
          successButtonLabel={t('h24_login_with_success')}
          eventName={Event.LoginFormClick}
          trackingParams={{ provider: AuthenticationProvider.Home24Password }}
          py="sp_12"
          mb="sp_12"
        />
      )}
    />
  );
};

export default Form;
